import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { API, useAuth } from "../App";
import QMSLayout from "../components/QMSLayout";
import axios from "axios";
import {
  Shield, Loader2, Plus, Search, Eye, FileText, CheckCircle2,
  XCircle, AlertTriangle, Clock, ChevronRight, ArrowLeft,
  Filter, Lock, Unlock
} from "lucide-react";

const ax = axios.create({ withCredentials: true });

const STATUS_STYLES = {
  queued: { bg: "bg-slate-400/10", text: "text-slate-400", label: "Queued" },
  analyzing: { bg: "bg-blue-400/10", text: "text-blue-400", label: "Analyzing" },
  preview_ready: { bg: "bg-amber-400/10", text: "text-amber-400", label: "Preview Ready" },
  completed: { bg: "bg-emerald-400/10", text: "text-emerald-400", label: "Completed" },
  failed: { bg: "bg-red-400/10", text: "text-red-400", label: "Failed" },
};

const CONF_STYLES = {
  high: "text-emerald-400",
  medium: "text-amber-400",
  low: "text-orange-400",
};

const SOURCE_TYPES = ["document", "evidence", "package", "summary", "scriber_note_export"];

export default function RedactionPage() {
  const { t } = useTranslation('operations');
  const { user } = useAuth();
  const [tab, setTab] = useState("jobs");
  const [jobs, setJobs] = useState([]);
  const [rulesets, setRulesets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [showNewJob, setShowNewJob] = useState(false);
  const [showNewRuleset, setShowNewRuleset] = useState(false);

  const loadJobs = useCallback(async () => {
    try {
      const res = await ax.get(`${API}/redaction/jobs`);
      setJobs(res.data.jobs || []);
    } catch {}
  }, []);

  const loadRulesets = useCallback(async () => {
    try {
      const res = await ax.get(`${API}/redaction/rulesets`);
      setRulesets(res.data.rulesets || []);
    } catch {}
  }, []);

  useEffect(() => {
    Promise.all([loadJobs(), loadRulesets()]).then(() => setLoading(false));
  }, [loadJobs, loadRulesets]);

  const createJob = async (data) => {
    try {
      const res = await ax.post(`${API}/redaction/jobs`, data);
      setShowNewJob(false);
      await loadJobs();
      setSelectedJob(res.data);
      setTab("jobs");
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to create job");
    }
  };

  const createRuleset = async (data) => {
    try {
      await ax.post(`${API}/redaction/rulesets`, data);
      setShowNewRuleset(false);
      loadRulesets();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to create ruleset");
    }
  };

  const loadPreview = async (jobId) => {
    try {
      const res = await ax.get(`${API}/redaction/jobs/${jobId}/preview`);
      setPreviewData(res.data);
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to load preview");
    }
  };

  const applyRedaction = async (jobId, confirmedEntities = []) => {
    if (!window.confirm(t('redaction.applyConfirm'))) return;
    try {
      await ax.post(`${API}/redaction/jobs/${jobId}/apply`, { confirmed_entities: confirmedEntities });
      setPreviewData(null);
      setSelectedJob(null);
      loadJobs();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to apply redaction");
    }
  };

  if (loading) {
    return (
      <QMSLayout title={t('redaction.title')}>
        <div className="min-h-[60vh] flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-[#36A9E1] animate-spin" />
        </div>
      </QMSLayout>
    );
  }

  // Preview Detail View
  if (previewData) {
    return (
      <QMSLayout title={t('operations:redaction.title')}>
        <div className="space-y-4" data-testid="redaction-preview">
          <button onClick={() => setPreviewData(null)} className="flex items-center gap-2 text-[#8B95A5] hover:text-white text-sm" data-testid="back-from-preview">
            <ArrowLeft className="w-4 h-4" /> Back to Job
          </button>

          {/* Guardrails Banner */}
          <div className="bg-emerald-400/10 border border-emerald-400/20 rounded-lg p-4 flex items-center gap-3" data-testid="original-preserved-banner">
            <Lock className="w-5 h-5 text-emerald-400" />
            <div>
              <div className="text-emerald-400 font-semibold text-sm">Original File Preserved</div>
              <div className="text-xs text-[#8B95A5]">Redacted copy will be generated separately. Ruleset: {previewData.ruleset_id} v{previewData.ruleset_version}</div>
            </div>
          </div>

          {/* Entity Summary */}
          <div className="bg-[#181B23] rounded-lg p-4 border border-[#2A2D38]" data-testid="entity-summary">
            <h3 className="text-white font-semibold mb-3">Detected Entities ({previewData.total_redacted})</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {Object.entries(previewData.entity_count_by_type || {}).map(([type, count]) => (
                <div key={type} className="bg-[#1F222D] rounded p-2 text-center">
                  <div className="text-white font-bold">{count}</div>
                  <div className="text-xs text-[#8B95A5]">{type.replace(/_/g, " ")}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Ambiguous Entities Warning */}
          {previewData.ambiguous_entities?.length > 0 && (
            <div className="bg-amber-400/10 border border-amber-400/20 rounded-lg p-4" data-testid="ambiguous-warning">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-amber-400" />
                <span className="text-amber-400 font-semibold text-sm">Review Required — Possible Sensitive Entities</span>
              </div>
              <p className="text-xs text-[#8B95A5] mb-3">These entities were detected heuristically and require manual reviewer confirmation before redaction.</p>
              <div className="space-y-1">
                {previewData.ambiguous_entities.map((e, i) => (
                  <div key={i} className="flex items-center justify-between bg-[#1F222D] rounded px-3 py-2 text-sm">
                    <span className="text-white">&quot;{e.text}&quot;</span>
                    <span className={`text-xs ${CONF_STYLES[e.confidence] || "text-slate-400"}`}>{e.label} ({e.confidence})</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Redacted Preview Text */}
          <div className="bg-[#181B23] rounded-lg p-4 border border-[#2A2D38]" data-testid="redacted-preview-text">
            <h3 className="text-white font-semibold mb-2">Redacted Preview</h3>
            <pre className="text-sm text-[#8B95A5] whitespace-pre-wrap bg-[#0D0F14] rounded p-3 max-h-64 overflow-auto">{previewData.redacted_preview}</pre>
          </div>

          <div className="flex gap-3 justify-end">
            <button onClick={() => setPreviewData(null)} className="px-4 py-2 rounded bg-[#1F222D] text-[#8B95A5] hover:bg-[#2A2D38]">{t('common:actions.cancel')}</button>
            <button
              onClick={() => applyRedaction(previewData.job_id, previewData.ambiguous_entities?.map(e => e.text) || [])}
              className="px-4 py-2 rounded bg-[#36A9E1] text-white hover:bg-[#2B8BC2] flex items-center gap-2"
              data-testid="apply-redaction-btn"
            >
              <CheckCircle2 className="w-4 h-4" /> Apply Redaction
            </button>
          </div>
        </div>
      </QMSLayout>
    );
  }

  // Job Detail View
  if (selectedJob) {
    const st = STATUS_STYLES[selectedJob.status] || STATUS_STYLES.queued;
    return (
      <QMSLayout title={t('operations:redaction.title')}>
        <div className="space-y-4" data-testid="redaction-job-detail">
          <button onClick={() => setSelectedJob(null)} className="flex items-center gap-2 text-[#8B95A5] hover:text-white text-sm" data-testid="back-from-detail">
            <ArrowLeft className="w-4 h-4" /> Back to Jobs
          </button>

          <div className="bg-[#181B23] rounded-lg p-5 border border-[#2A2D38]">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-white font-bold text-lg">{selectedJob.job_id}</h2>
              <span className={`px-3 py-1 rounded text-xs ${st.bg} ${st.text}`}>{st.label}</span>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm mb-4">
              <div><span className="text-[#8B95A5]">Source Type:</span> <span className="text-white ml-2">{selectedJob.source_type}</span></div>
              <div><span className="text-[#8B95A5]">File:</span> <span className="text-white ml-2">{selectedJob.file_name}</span></div>
              <div><span className="text-[#8B95A5]">Ruleset:</span> <span className="text-white ml-2">{selectedJob.ruleset_id} v{selectedJob.ruleset_version}</span></div>
              <div><span className="text-[#8B95A5]">Requested By:</span> <span className="text-white ml-2">{selectedJob.requested_by_name}</span></div>
              <div><span className="text-[#8B95A5]">Requested At:</span> <span className="text-white ml-2">{new Date(selectedJob.requested_at).toLocaleString()}</span></div>
              {selectedJob.pages_impacted > 0 && <div><span className="text-[#8B95A5]">Sections Impacted:</span> <span className="text-white ml-2">{selectedJob.pages_impacted}</span></div>}
            </div>

            {selectedJob.preview_summary && (
              <div className="bg-[#0D0F14] rounded p-3 mb-4 text-sm text-[#8B95A5]" data-testid="preview-summary">{selectedJob.preview_summary}</div>
            )}

            {/* Entity counts */}
            {Object.keys(selectedJob.entity_count_by_type || {}).length > 0 && (
              <div className="grid grid-cols-3 md:grid-cols-5 gap-2 mb-4" data-testid="entity-counts">
                {Object.entries(selectedJob.entity_count_by_type).map(([type, count]) => (
                  <div key={type} className="bg-[#0D0F14] rounded p-2 text-center">
                    <div className="text-white font-bold text-sm">{count}</div>
                    <div className="text-xs text-[#8B95A5]">{type.replace(/_/g, " ")}</div>
                  </div>
                ))}
              </div>
            )}

            {selectedJob.original_file_reference && (
              <div className="bg-emerald-400/10 rounded p-2 text-xs text-emerald-400 mb-4 flex items-center gap-2" data-testid="original-ref">
                <Lock className="w-3 h-3" /> Original preserved: {selectedJob.original_file_reference}
              </div>
            )}

            {selectedJob.error_message && (
              <div className="bg-red-400/10 rounded p-3 text-sm text-red-400 mb-4">
                <AlertTriangle className="w-4 h-4 inline mr-2" />{selectedJob.error_message}
              </div>
            )}

            <div className="flex gap-3">
              {selectedJob.status === "preview_ready" && (
                <button onClick={() => loadPreview(selectedJob.job_id)} className="px-4 py-2 rounded bg-[#36A9E1] text-white hover:bg-[#2B8BC2] flex items-center gap-2" data-testid="view-preview-btn">
                  <Eye className="w-4 h-4" /> View Preview
                </button>
              )}
              {selectedJob.status === "completed" && (
                <button onClick={async () => {
                  try {
                    const res = await ax.get(`${API}/redaction/jobs/${selectedJob.job_id}/redacted`);
                    setPreviewData({ ...res.data, redacted_preview: res.data.redacted_text, job_id: selectedJob.job_id, ruleset_id: selectedJob.ruleset_id, ruleset_version: selectedJob.ruleset_version, entity_count_by_type: selectedJob.entity_count_by_type, total_redacted: 0, sections_impacted: 0, ambiguous_entities: [] });
                  } catch {}
                }} className="px-4 py-2 rounded bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 flex items-center gap-2" data-testid="view-redacted-btn">
                  <FileText className="w-4 h-4" /> View Redacted Output
                </button>
              )}
            </div>
          </div>
        </div>
      </QMSLayout>
    );
  }

  return (
    <QMSLayout title={t('operations:redaction.title')}>
      <div className="space-y-4" data-testid="redaction-page">
        {/* Tabs */}
        <div className="flex items-center justify-between">
          <div className="flex gap-1 bg-[#181B23] rounded-lg p-1">
            {[{ id: "jobs", label: "Redaction Jobs" }, { id: "rulesets", label: "Rulesets" }].map(item => (
              <button key={item.id} onClick={() => setTab(item.id)}
                className={`px-4 py-2 rounded text-sm transition-colors ${tab === item.id ? "bg-[#36A9E1]/10 text-[#36A9E1]" : "text-[#8B95A5] hover:text-white"}`}
                data-testid={`tab-${item.id}`}
              >{item.label}</button>
            ))}
          </div>
          <div className="flex gap-2">
            {tab === "jobs" && (
              <button onClick={() => setShowNewJob(true)} className="px-3 py-2 rounded bg-[#36A9E1] text-white text-sm flex items-center gap-2 hover:bg-[#2B8BC2]" data-testid="new-job-btn">
                <Plus className="w-4 h-4" /> New Redaction Job
              </button>
            )}
            {tab === "rulesets" && user?.role === "admin" && (
              <button onClick={() => setShowNewRuleset(true)} className="px-3 py-2 rounded bg-[#36A9E1] text-white text-sm flex items-center gap-2 hover:bg-[#2B8BC2]" data-testid="new-ruleset-btn">
                <Plus className="w-4 h-4" /> New Ruleset
              </button>
            )}
          </div>
        </div>

        {/* Jobs List */}
        {tab === "jobs" && (
          <div className="space-y-2" data-testid="jobs-list">
            {jobs.length === 0 ? (
              <div className="bg-[#181B23] rounded-lg p-8 text-center text-[#8B95A5]">
                <Shield className="w-10 h-10 mx-auto mb-3 opacity-40" />
                <p>No redaction jobs yet. Create one to start.</p>
              </div>
            ) : jobs.map(j => {
              const st = STATUS_STYLES[j.status] || STATUS_STYLES.queued;
              return (
                <div key={j.job_id} onClick={() => setSelectedJob(j)}
                  className="bg-[#181B23] rounded-lg p-4 border border-[#2A2D38] hover:border-[#36A9E1]/30 cursor-pointer transition-colors"
                  data-testid={`job-${j.job_id}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-white font-medium text-sm">{j.job_id}</div>
                      <div className="text-xs text-[#8B95A5] mt-1">{j.source_type} &middot; {j.file_name} &middot; {new Date(j.requested_at).toLocaleDateString()}</div>
                    </div>
                    <div className="flex items-center gap-3">
                      {j.pages_impacted > 0 && <span className="text-xs text-[#8B95A5]">{j.pages_impacted} sections</span>}
                      <span className={`px-2 py-1 rounded text-xs ${st.bg} ${st.text}`}>{st.label}</span>
                      <ChevronRight className="w-4 h-4 text-[#8B95A5]" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Rulesets List */}
        {tab === "rulesets" && (
          <div className="space-y-2" data-testid="rulesets-list">
            {rulesets.length === 0 ? (
              <div className="bg-[#181B23] rounded-lg p-8 text-center text-[#8B95A5]">
                <Filter className="w-10 h-10 mx-auto mb-3 opacity-40" />
                <p>No rulesets configured. Admin can create one.</p>
              </div>
            ) : rulesets.map(r => (
              <div key={r.ruleset_id} className="bg-[#181B23] rounded-lg p-4 border border-[#2A2D38]" data-testid={`ruleset-${r.ruleset_id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-white font-medium text-sm">{r.name}</div>
                    <div className="text-xs text-[#8B95A5] mt-1">v{r.version} &middot; {(r.entity_types || []).length} entity types &middot; {r.active ? t('operations:redaction.active') : t('operations:redaction.inactive')}</div>
                  </div>
                  <span className={`px-2 py-1 rounded text-xs ${r.active ? "bg-emerald-400/10 text-emerald-400" : "bg-red-400/10 text-red-400"}`}>
                    {r.active ? t('operations:redaction.active') : t('operations:redaction.inactive')}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* New Job Modal */}
        {showNewJob && <NewJobModal rulesets={rulesets} onSubmit={createJob} onClose={() => setShowNewJob(false)} />}
        {showNewRuleset && <NewRulesetModal onSubmit={createRuleset} onClose={() => setShowNewRuleset(false)} />}
      </div>
    </QMSLayout>
  );
}

function NewJobModal({ rulesets, onSubmit, onClose }) {
  const { t } = useTranslation(['operations', 'common']);
  const [sourceType, setSourceType] = useState("document");
  const [sourceId, setSourceId] = useState("");
  const [sourceText, setSourceText] = useState("");
  const [fileName, setFileName] = useState("");
  const [rulesetId, setRulesetId] = useState("");

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="new-job-modal">
      <div className="bg-[#181B23] rounded-xl border border-[#2A2D38] w-full max-w-lg p-6">
        <h2 className="text-white font-bold text-lg mb-4">New Redaction Job</h2>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-[#8B95A5] block mb-1">Source Type</label>
            <select value={sourceType} onChange={e => setSourceType(e.target.value)} className="w-full bg-[#0D0F14] border border-[#2A2D38] rounded px-3 py-2 text-white text-sm" data-testid="source-type-select">
              {SOURCE_TYPES.map(item => <option key={item} value={item}>{item.replace(/_/g, " ")}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-[#8B95A5] block mb-1">Source ID</label>
            <input value={sourceId} onChange={e => setSourceId(e.target.value)} placeholder="e.g. DOC-ABC123" className="w-full bg-[#0D0F14] border border-[#2A2D38] rounded px-3 py-2 text-white text-sm" data-testid="source-id-input" />
          </div>
          <div>
            <label className="text-xs text-[#8B95A5] block mb-1">File Name</label>
            <input value={fileName} onChange={e => setFileName(e.target.value)} placeholder="audit_report.txt" className="w-full bg-[#0D0F14] border border-[#2A2D38] rounded px-3 py-2 text-white text-sm" data-testid="file-name-input" />
          </div>
          <div>
            <label className="text-xs text-[#8B95A5] block mb-1">Source Text</label>
            <textarea value={sourceText} onChange={e => setSourceText(e.target.value)} rows={5} placeholder="Paste text content to redact..." className="w-full bg-[#0D0F14] border border-[#2A2D38] rounded px-3 py-2 text-white text-sm" data-testid="source-text-input" />
          </div>
          <div>
            <label className="text-xs text-[#8B95A5] block mb-1">Ruleset (optional)</label>
            <select value={rulesetId} onChange={e => setRulesetId(e.target.value)} className="w-full bg-[#0D0F14] border border-[#2A2D38] rounded px-3 py-2 text-white text-sm" data-testid="ruleset-select">
              <option value="">Default Ruleset</option>
              {rulesets.map(r => <option key={r.ruleset_id} value={r.ruleset_id}>{r.name} (v{r.version})</option>)}
            </select>
          </div>
        </div>
        <div className="flex gap-3 mt-5 justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded bg-[#1F222D] text-[#8B95A5] hover:bg-[#2A2D38] text-sm">{t('common:actions.cancel')}</button>
          <button onClick={() => onSubmit({ source_type: sourceType, source_id: sourceId, source_text: sourceText, file_name: fileName, ruleset_id: rulesetId || undefined })}
            disabled={!sourceText}
            className="px-4 py-2 rounded bg-[#36A9E1] text-white text-sm hover:bg-[#2B8BC2] disabled:opacity-40"
            data-testid="create-job-submit"
          >Create Job</button>
        </div>
      </div>
    </div>
  );
}

function NewRulesetModal({ onSubmit, onClose }) {
  const { t } = useTranslation(['operations', 'common']);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [version, setVersion] = useState("1.0");

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="new-ruleset-modal">
      <div className="bg-[#181B23] rounded-xl border border-[#2A2D38] w-full max-w-lg p-6">
        <h2 className="text-white font-bold text-lg mb-4">New Ruleset</h2>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-[#8B95A5] block mb-1">Name</label>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Clinical Trial Redaction" className="w-full bg-[#0D0F14] border border-[#2A2D38] rounded px-3 py-2 text-white text-sm" data-testid="ruleset-name-input" />
          </div>
          <div>
            <label className="text-xs text-[#8B95A5] block mb-1">Description</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} rows={2} className="w-full bg-[#0D0F14] border border-[#2A2D38] rounded px-3 py-2 text-white text-sm" data-testid="ruleset-desc-input" />
          </div>
          <div>
            <label className="text-xs text-[#8B95A5] block mb-1">Version</label>
            <input value={version} onChange={e => setVersion(e.target.value)} className="w-full bg-[#0D0F14] border border-[#2A2D38] rounded px-3 py-2 text-white text-sm" data-testid="ruleset-version-input" />
          </div>
        </div>
        <div className="flex gap-3 mt-5 justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded bg-[#1F222D] text-[#8B95A5] hover:bg-[#2A2D38] text-sm">{t('common:actions.cancel')}</button>
          <button onClick={() => onSubmit({ name, description, version })} disabled={!name}
            className="px-4 py-2 rounded bg-[#36A9E1] text-white text-sm hover:bg-[#2B8BC2] disabled:opacity-40"
            data-testid="create-ruleset-submit"
          >Create Ruleset</button>
        </div>
      </div>
    </div>
  );
}
